/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, ChevronRight, Plus, Search } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { interventiAPI } from '../api/interventi';
import { fmtDataOra } from '../utils/date';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#3B82F6';
const STATO_LABELS = { in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato' };
const PRIO = { urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente' }, alta: { bg: '#FED7AA', color: '#9A3412', label: 'Alta' }, normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' }, bassa: { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' } };

export default function Interventi() {
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [filtroStato, setFiltroStato] = useState('');
  const [q, setQ] = useState('');

  const { data: result = { data: [] }, isLoading } = useQuery({
    queryKey: ['interventi-cliente', filtroStato, q],
    queryFn: () => interventiAPI.list({ stato: filtroStato || undefined, q: q || undefined }),
  });

  return (
    <ClienteLayout title="I miei interventi">
      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={16} style={styles.searchIcon} />
          <input type="text" placeholder="Cerca..." value={q} onChange={(e) => setQ(e.target.value)} style={styles.search} />
        </div>
        <button onClick={() => navigate('/nuovo')} style={{ ...styles.addBtn, ...(isMobile ? { width: '100%', justifyContent: 'center' } : {}) }}><Plus size={16} /> Nuova richiesta</button>
      </div>

      <div style={{ ...styles.filters, ...(isMobile ? styles.filtersMobile : {}) }}>
        {['', 'in_attesa', 'confermato', 'in_corso', 'completato'].map((s) => (
          <button key={s} onClick={() => setFiltroStato(s)} style={{ ...styles.filterBtn, ...(filtroStato === s ? { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT } : {}) }}>
            {s === '' ? 'Tutti' : STATO_LABELS[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={styles.loading}>Caricamento...</div>
      ) : result.data.length === 0 ? (
        <div style={styles.empty}>
          <FileText size={40} color="#9CA3AF" />
          <p>Nessun intervento trovato.</p>
          <button onClick={() => navigate('/nuovo')} style={styles.addBtn}><Plus size={16} /> Richiedi il primo</button>
        </div>
      ) : (
        <div style={styles.list}>
          {result.data.map((i) => {
            const p = PRIO[i.priorita] || PRIO.normale;
            return (
              <div key={i.id} style={styles.card} onClick={() => navigate(`/interventi/${i.id}`)}>
                <div style={{ flex: 1 }}>
                  <div style={styles.cardHead}>
                    <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>{STATO_LABELS[i.stato]}</span>
                    <span style={{ ...styles.prioBadge, backgroundColor: p.bg, color: p.color }}>{p.label}</span>
                    <code style={styles.code}>{i.codice}</code>
                  </div>
                  <div style={{ fontWeight: 600, marginTop: '0.35rem' }}>{i.titolo}</div>
                  {i.descrizione && <div style={styles.desc}>{i.descrizione.substring(0, 100)}{i.descrizione.length > 100 ? '...' : ''}</div>}
                  <div style={styles.meta}>
                    {i.data_appuntamento ? <><Calendar size={13} /> {fmtDataOra(i.data_appuntamento)}</> : <span style={{ color: '#9CA3AF' }}>Data da concordare</span>}
                  </div>
                </div>
                <ChevronRight size={18} color="#9CA3AF" />
              </div>
            );
          })}
        </div>
      )}
    </ClienteLayout>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  toolbar: { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, minWidth: '200px' },
  searchIcon: { position: 'absolute', left: '0.8rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' },
  search: { width: '100%', padding: '0.6rem 0.85rem 0.6rem 2.2rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.92rem', outline: 'none' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  filters: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' },
  filtersMobile: { flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '0.25rem' },
  filterBtn: { padding: '0.55rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  card: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 },
  prioBadge: { padding: '0.18rem 0.5rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 600 },
  code: { backgroundColor: '#E5E7EB', padding: '0.12rem 0.4rem', borderRadius: '4px', fontSize: '0.76rem', fontFamily: 'ui-monospace, monospace' },
  desc: { color: '#6B7280', fontSize: '0.88rem', marginTop: '0.3rem' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', color: '#6B7280', marginTop: '0.4rem' },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', borderRadius: '12px' },
};
